import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
  FormHelperText,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { CreateProductCatalogPriceDto } from '@services/dtos'
import { useState } from 'react'
import { UpTransition } from '@components/animations'
import { ProductCatalogPriceForm } from './ProductCatalogPriceForm'
import moment from 'moment'
import { DeleteOutline, EditOutlined } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'
import { getCurrentPrice, getDatesPriceLabel } from '@utils/product.utils'
import ConfirmDialog from '@components/dialogs/ConfirmDialog'

type Props = {
  prices: CreateProductCatalogPriceDto[]
  appendPrice: (price: CreateProductCatalogPriceDto) => void
  removePrice: (index: number) => void
  updatePrice: (index: number, price: CreateProductCatalogPriceDto) => void
  error?: string
}

export function ProductCatalogPrices({ prices, appendPrice, removePrice, updatePrice, error }: Props) {
  const [priceFormOpen, setPriceFormOpen] = useState(false)
  const [editingPriceIndex, setEditingPriceIndex] = useState<number | null>(null)
  const [deletingPriceIndex, setDeletingPriceIndex] = useState<number | null>(null)

  const currentPrice = getCurrentPrice(prices)

  return (
    <>
      <fieldset className="border border-gray-200 p-4 rounded-lg">
        <legend className="text-sm font-medium text-gray-700 mb-1 border border-gray-200 p-2 rounded-lg">
          Tarification
        </legend>

        {error && <FormHelperText error>{error}</FormHelperText>}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: (t) => (t.palette.mode === 'light' ? 'grey.50' : 'background.paper'),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="body1">
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Prix actuel : {currentPrice ? `${currentPrice?.priceExclVat.toFixed(2)} € HT` : 'à renseigner'}
                </Box>{' '}
              </Typography>
              {currentPrice && (<>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                  TVA : {currentPrice.vatRate * 100}%, 
                  Prix TTC : {(currentPrice.priceExclVat * (1 + currentPrice.vatRate)).toFixed(2)} €
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                  {getDatesPriceLabel(currentPrice)}
                </Typography>
              </>)}
            </Box>
          </Stack>
        </Paper>

        {prices.length > 0 && (<>
          <Divider sx={{ my: 2 }} />
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table size="small" aria-label="Historique des prix">
              <TableHead>
                <TableRow>
                  {['Début', 'Fin', 'Prix HT', 'TVA', 'Prix TTC'].map((label) => (
                    <TableCell key={label} sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      {label}
                    </TableCell>
                  ))}
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {prices.map(({ startAt, endAt, vatRate, priceExclVat }, index) => (
                  <TableRow key={`price-${uuidv4()}`} hover>
                    <TableCell>{startAt ? moment(startAt).format('DD/MM/YYYY') : '-'}</TableCell>
                    <TableCell>{endAt ? moment(endAt).format('DD/MM/YYYY') : '-'}</TableCell>
                    <TableCell>{priceExclVat.toFixed(2)} €</TableCell>
                    <TableCell>{vatRate * 100}%</TableCell>
                    <TableCell>{(priceExclVat * (1 + vatRate)).toFixed(2)} €</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Supprimer">
                        <IconButton size="small" aria-label="Supprimer" onClick={() => setDeletingPriceIndex(index)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <IconButton size="small" aria-label="Modifier" onClick={() => {
                          setPriceFormOpen(true)
                          setEditingPriceIndex(index)
                        }}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>)}

        <Button
          sx={{ mt: 1 }}
          startIcon={<AddIcon />}
          onClick={() => setPriceFormOpen(true)}
        >
          Ajouter une période de prix
        </Button>
      </fieldset>

      <Dialog
        open={!!priceFormOpen}
        onClose={() => setPriceFormOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          <ProductCatalogPriceForm 
            onClose={() => {
              setPriceFormOpen(false)
              setEditingPriceIndex(null)
            }} 
            appendPrice={appendPrice} 
            updatePrice={(data) => {
              if (editingPriceIndex != null) {
                updatePrice(editingPriceIndex, data)
              }
            }}
            editingPrice={editingPriceIndex !== null ? prices[editingPriceIndex] : null}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deletingPriceIndex !== null}
        title="Confirmation"
        message={'Êtes-vous sûr de vouloir supprimer ce prix ?'}
        onConfirm={() => {
          if (deletingPriceIndex === null) {
            return
          }
          removePrice(deletingPriceIndex)
          setDeletingPriceIndex(null)
        }}
        onCancel={() => setDeletingPriceIndex(null)}
      />
    </>
  )
}
